import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarIcon, Clock, MapPin, ArrowRight, Sparkles, Users, Settings } from "lucide-react";
import axios from "axios";
import logger from "@/lib/logger";
import Marquee from "react-fast-marquee";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eventTypeConfig = {
  workshop: { color: "bg-soar-teal", icon: Sparkles, label: "Workshop" },
  program: { color: "bg-[#212163]", icon: Users, label: "Program" },
  deadline: { color: "bg-soar-gold", icon: CalendarIcon, label: "Deadline" },
};

const EventCard = ({ event }) => {
  const config = eventTypeConfig[event.event_type] || eventTypeConfig.program;
  const Icon = config.icon;
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <div className="card-elevated rounded-3xl overflow-hidden group">
      <div className={`${config.color} p-4`}>
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-bold bg-soar-blue/20 text-soar-blue px-2.5 py-0.5 rounded-full uppercase tracking-wider">
            {config.label}
          </span>
          <Icon className="w-5 h-5 text-soar-blue/80" aria-hidden="true" />
        </div>
      </div>
      <div className="p-6">
        <h3 className="font-heading text-xl text-soar-blue mb-2 group-hover:text-soar-teal transition-colors duration-200 tracking-tight">
          {event.title}
        </h3>
        <p className="text-soar-blue/50 text-sm mb-4 line-clamp-2">{event.description}</p>
        
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-soar-blue/70">
            <CalendarIcon className="w-4 h-4 text-soar-gold" />
            <span>{formatDate(event.date)}</span>
          </div>
          
          {event.start_time && event.start_time !== "TBD" && (
            <div className="flex items-center gap-2 text-soar-blue/50">
              <Clock className="w-4 h-4 text-soar-teal" />
              <span>{event.start_time}{event.end_time && event.end_time !== "TBD" ? ` - ${event.end_time}` : ''}</span>
            </div>
          )}
          
          {event.location && event.location !== "TBD" && (
            <div className="flex items-center gap-2 text-soar-blue/50">
              <MapPin className="w-4 h-4 text-soar-teal" />
              <span>{event.location}</span>
            </div>
          )}
        </div>

        {event.registration_open && !event.is_deadline && (
          <Link to={`/register?event=${event.id}&title=${encodeURIComponent(event.title)}`} className="mt-4 block">
            <Button className="w-full rounded-full bg-soar-teal hover:bg-soar-teal/90 text-sm font-bold">
              Register Now
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
};

const Calendar = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchEvents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/calendar`);
      const sortedEvents = response.data.sort((a, b) => 
        new Date(a.date) - new Date(b.date)
      );
      setEvents(sortedEvents);
    } catch (error) {
      logger.error("Failed to fetch events:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const filteredEvents = filter === "all" 
    ? events 
    : events.filter(e => e.event_type === filter);

  const upcomingEvents = filteredEvents.filter(e => new Date(e.date) >= new Date());

  const renderEventsContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-20">
          <div className="animate-spin w-8 h-8 border-2 border-soar-teal border-t-transparent rounded-full"></div>
        </div>
      );
    }
    if (upcomingEvents.length === 0) {
      return (
        <div className="text-center py-20">
          <CalendarIcon className="w-16 h-16 text-white/10 mx-auto mb-4" aria-hidden="true" />
          <h3 className="font-heading text-2xl text-soar-blue mb-2 tracking-tight">No Events Found</h3>
          <p className="text-soar-blue/50">Check back soon for upcoming events!</p>
        </div>
      );
    }
    return (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {upcomingEvents.map((event) => (
          <EventCard key={event.id} event={event} />
        ))}
      </div>
    );
  };

  return (
    <div data-testid="calendar-page" className="bg-white">

      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/7096/people-woman-coffee-meeting.jpg?w=1600"
            alt="Calendar"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-soar-blue via-soar-blue/95 to-soar-blue/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="max-w-3xl space-y-6">
            <div className="flex items-center justify-between">
              <span className="inline-block border border-soar-gold/40 rounded-full px-4 py-1.5 text-xs font-semibold tracking-widest text-soar-gold uppercase opacity-0 animate-fade-up" style={{ animationFillMode: 'forwards' }}>
                Upcoming Events
              </span>
              <Link 
                to="/admin/calendar" 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white/80 hover:text-white text-sm font-medium transition-colors opacity-0 animate-fade-up"
                style={{ animationFillMode: 'forwards' }}
                data-testid="admin-calendar-link"
              >
                <Settings className="w-4 h-4" /> Manage Events
              </Link>
            </div>
            <h1 className="font-heading text-6xl sm:text-7xl lg:text-8xl leading-none tracking-tight text-white opacity-0 animate-fade-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
              Event <span className="text-soar-gold">Calendar</span>
            </h1>
            <p className="text-xl text-white/80 opacity-0 animate-fade-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
              Stay connected with SOAR Solutions! Check out our upcoming workshops, programs, and important dates.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap gap-3 justify-center">
            <Button
              onClick={() => setFilter("all")}
              className={`rounded-full ${filter === "all" ? "bg-soar-blue/10 text-white" : "bg-transparent text-soar-blue/50 hover:text-white"} border border-soar-blue/10`}
            >
              All Events
            </Button>
            <Button
              onClick={() => setFilter("workshop")}
              className={`rounded-full ${filter === "workshop" ? "bg-soar-teal text-white" : "bg-transparent text-soar-blue/50 hover:text-white"} border border-soar-blue/10`}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Workshops
            </Button>
            <Button
              onClick={() => setFilter("program")}
              className={`rounded-full ${filter === "program" ? "bg-[#212163] text-white" : "bg-transparent text-soar-blue/50 hover:text-white"} border border-soar-blue/10`}
            >
              <Users className="w-4 h-4 mr-2" />
              Programs
            </Button>
            <Button
              onClick={() => setFilter("deadline")}
              className={`rounded-full ${filter === "deadline" ? "bg-soar-gold text-[#0a0a2e]" : "bg-transparent text-soar-blue/50 hover:text-white"} border border-soar-blue/10`}
            >
              <CalendarIcon className="w-4 h-4 mr-2" />
              Deadlines
            </Button>
          </div>
        </div>
      </section>

      {/* Events Grid */}
      <section className="py-20" data-testid="events-grid">
        <div className="max-w-7xl mx-auto px-6">
          {renderEventsContent()}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 relative overflow-hidden" data-testid="calendar-cta">
        <div className="absolute inset-0 bg-soar-teal" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-4xl md:text-5xl text-soar-blue mb-4 tracking-tight">Don't Miss Out!</h2>
          <p className="text-soar-blue/80 mb-8 max-w-2xl mx-auto">
            Follow us on social media for event updates, registration reminders, and more!
          </p>
          <Link to="/contact">
            <Button className="rounded-full bg-white text-soar-teal hover:bg-white/90 px-8 py-6 text-base font-bold hover:scale-105 transition-transform duration-200">
              Contact Us <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Calendar;
